import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import ReactGA from 'react-ga4';
import settings from "../../libs/config";
import Logger from '../../functions/logging';
import * as auth from '../../functions/auth';
import * as helpers from '../../functions/helpers';
import * as studyFns from '../../functions/study';
import { formatDuration } from '../../functions/time';

import FadeIn from '../global/FadeIn';
import { calculatePoints } from '../../functions/rewards';
import { getPersistentValue, getCookieValue } from '../../functions/persistent';
import * as TargetProcess from '../../functions/targetProcess';
import * as codes from "../../constants/feedbackCodes";
import localeMessages from '../../locales/feedback';
import DoorbellAudio from '../../static/sounds/voxeet_reaching_out.mp3';

/* Google analytics */
import SessionStatus from '../../analytics/analytics-events/SessionStatus';
import { SessionStatusEvent } from '../../analytics/analytics-events/constants';

import { getUser, updateViewerMetadata } from '../../api/user';
import { setViewerCookies, getRedirectToReelChat } from '../../functions/helpers';
import { setPanelPartner } from '../../redux/actions/index';
import '../../static/Feedback.css';
import DeviceHelper from '../../functions/device';
import PilotlyModal from './PilotlyModal';

const PARTNER_KEY = "pil_partnerkey";
const PARTNER_ID = "pil_partnerid";
const LINKED_MODERATED_STUDY = 'linked_moderated_study';
const PARTNER_NAME = "pil_partnerName";
const REELCHAT_SOURCE_ID = "rc";

ReactGA.initialize([{ trackingId: settings.gaCode }]);

class Feedback extends Component {
	constructor(props) {
		super(props);

		this.state = {
			waitingTime: 0
		};

		this.didShowRedirectDialog = false;
		this.interval = null;
		this.installedChrome = false;
		this.redirectTimeout = null;

		this.getFeedbackDisplay = this.getFeedbackDisplay.bind(this);
		this.getUpdatedCurrentUser = this.getUpdatedCurrentUser.bind(this);
		this.gotoRedirect = this.gotoRedirect.bind(this);
		this.handleViewerMetaData = this.handleViewerMetaData.bind(this);
		this.handleViewerMetaDataOnClose = this.handleViewerMetaDataOnClose.bind(this);
		this.moveViewerToReelChat = this.moveViewerToReelChat.bind(this);
		this.onBeforeUnload = this.onBeforeUnload.bind(this);
		this.onChangeVisibility = this.onChangeVisibility.bind(this);
		this.redirectToGooglePlay = this.redirectToGooglePlay.bind(this);
	}
	
	componentWillUnmount() {
		if (this.interval) {
			clearInterval(this.interval);
			this.interval = null;
		}
	}

	componentDidMount() {
		Logger.log(this.constructor.name, "componentDidMount()", this.props);

		const { feedbackType, fetchedGroup, targetParams, currentUser } = this.props;

		let type = feedbackType;
		let groupId = fetchedGroup ? fetchedGroup._id : (targetParams ? targetParams.gid : null);
		let paramsObject = targetParams;
		this.props.setPanelPartner(null);

		// We may be coming to this page from an external source, e.g. reelchat
		// so don't have targetParams and group. Need to load
		if (!targetParams) {
			const savedUrl = auth.getSavedUrl();
			Logger.log(this.constructor.name, "componentDidMount() savedUrl", savedUrl);
			paramsObject = TargetProcess.parseUrl(savedUrl);

			paramsObject = TargetProcess.decodeRedirects(paramsObject);

			const partnerkey = getPersistentValue(PARTNER_KEY);
			const partnerid = getPersistentValue(PARTNER_ID);
			if (partnerkey && partnerid) {
				paramsObject[partnerkey] = partnerid;
				paramsObject = TargetProcess.partnerIdReplace(paramsObject);
			}

			if (paramsObject.gid) {
				groupId = paramsObject.gid;
			}

			type = this.getFeedbackTypeFromUrl();

			this.updateState({
				feedbackType: type,
				targetParams: paramsObject
			});
		}

		if (fetchedGroup && fetchedGroup.data_type === LINKED_MODERATED_STUDY && feedbackType === 'waitingroom') {
			window.addEventListener("beforeunload", this.onBeforeUnload);

			this.getUpdatedCurrentUser(this.handleViewerMetaData);

			this.interval = setInterval(() => {
				this.getUpdatedCurrentUser(this.handleViewerMetaData);
				this.setState({ waitingTime: this.state.waitingTime + 1 });
			}, 1000);
		}

		Logger.log(this.constructor.name, "componentDidMount() paramsObject", paramsObject);

		this.handleIntialRedirect(type, paramsObject);

		if (groupId && type) {
			this.storeStudyStatus(groupId, type);
		}
	}

	async getUpdatedCurrentUser(callback) {
		const { fetchedGroup, currentUser } = this.props;
		let user_token = auth.getToken();

		try {
			const result = await getUser(user_token);
			if (result) {
				Logger.log(this.constructor.name, "getUpdatedCurrentUser() User Success", result, currentUser);
				this.props.setCurrentUser(result);
				if (callback) callback(result);
			}
			else {
				Logger.log(this.constructor.name, "getUpdatedCurrentUser() User Failed: No result token???");
				let errors = { ...this.state.errors };
				errors["reason"] = this.props.intl.formatMessage(localeMessages.error_loading_user);
				this.setState({ errors });
			}
		}
		catch (error) {
			let errors = { ...this.state.errors };
			Logger.log(this.constructor.name, "getUpdatedCurrentUser() User Failed", error);
			errors["status"] = this.props.intl.formatMessage(localeMessages.error_loading_user);
			this.setState({ errors });
		}
	}

	async handleViewerMetaData(currentUser, updatedMetadata, callback, preResultFunction) {
		const { fetchedGroup, targetParams } = this.props;

		let user_token = auth.getToken();
		let psIndex = targetParams.panelSegmentIndex || targetParams.ps || 0;
		const isReelChat = targetParams && targetParams.rch === '1';

		try {
			let method = 'create';
			let metadata = helpers.getViewerMetadata(currentUser, fetchedGroup);
			let metadataFields = {};
			let date = new Date();
			if (isReelChat) metadataFields.waiting_room_time = date.toString();

			if (metadata 
				&& currentUser
				&& currentUser.viewer_metadata 
				&& currentUser.viewer_metadata.length
			) {
				method = 'update';
				metadataFields._id = metadata._id;
			}

			if (updatedMetadata 
				&& (!metadata.reelchat 
				|| (!metadata.reelchat.invite 
				&& !metadata.reelchat.dismissed))
			) {
				metadataFields = Object.assign(metadataFields, updatedMetadata);
			}

			if (preResultFunction) { 
				preResultFunction(); 
				Logger.log(this.constructor.name, "handleViewerMetaData() Cleared AUTH");
			}

			const result = currentUser ? await updateViewerMetadata(currentUser.user_id, user_token, psIndex, fetchedGroup._id, metadataFields, method) : undefined;

			if (result) {
				Logger.log(this.constructor.name, "handleViewerMetaData() Meta Data Successful", result);

				let viewerMetadata = result.value;
				if (viewerMetadata 
					&& viewerMetadata.reelchat 
					&& viewerMetadata.reelchat.dismissed
				) {
					this.setState({ feedbackType: 'dismissed' }, () => {
						clearInterval(this.interval);
						this.interval = null;
						auth.logout();
					});
				}
				else if (viewerMetadata 
					&& viewerMetadata.reelchat
					&& viewerMetadata.reelchat.invite
					&& (viewerMetadata.reelchat.moderated_panel_segment || viewerMetadata.reelchat.moderated_panel_segment === 0)
					&& fetchedGroup.panel_segment
					&& fetchedGroup.panel_segment.length
				) {
					/* -- NOTE --
						Right now, we're only defaulting the ps index to the first panel segment that is enabled 
						as a moderated discussion. In the future, we'll be linking specific panel segments together
					*/
					let reelchatPSIndex = viewerMetadata.reelchat.moderated_panel_segment;

					if (reelchatPSIndex || reelchatPSIndex === 0) {
						this.moveViewerToReelChat(reelchatPSIndex, viewerMetadata);
					}
				}
				else if (callback) {
					callback();
				}
			}
			else {
				Logger.log(this.constructor.name, "handleViewerMetaData() Meta Data Failed: No result token???");
				let errors = { ...this.state.errors };
				errors["status"] = this.props.intl.formatMessage(localeMessages.error_metadata_failed);
				this.setState({ errors });
			}
		}
		catch (error) {
			let errors = { ...this.state.errors };
			Logger.log(this.constructor.name, "handleViewerMetaData() Meta Data Failed", error);

			if (error.error === 403 || error.error === "accounts") {
				errors["status"] = this.props.intl.formatMessage(localeMessages.error_metadata_failed);
			}
			else {
				errors["status"] = this.props.intl.formatMessage(localeMessages.error_metadata_failed);
			}
			this.setState({ errors });
		}
	}

	onBeforeUnload(event) {
		event.preventDefault();
		this.handleViewerMetaDataOnClose(null);
	}

	async moveViewerToReelChat(psIndex, viewerMetadata) {
		clearInterval(this.interval);
		this.interval = null;

		const audio = new Audio(DoorbellAudio);
		audio.play();

		setTimeout(() => {
			let { targetParams, fetchedGroup, fetchedEpisode, currentUser } = this.props;
			Logger.log(Feedback.name, "moveViewerToReelChat() isModerated", targetParams, fetchedGroup);

			setViewerCookies(targetParams, fetchedGroup, fetchedEpisode, currentUser, viewerMetadata);
			let reelchatUrl = getRedirectToReelChat(targetParams, psIndex);

			Logger.log(Feedback.name, "moveViewerToReelChat() isModerated reelchatUrl", reelchatUrl);
			auth.logout();
			window.removeEventListener("beforeunload", this.onBeforeUnload);
			window.location.replace(reelchatUrl);
		}, 2000);
	}

	updateState(newState) {
		if (newState) {
			this.setState(newState);
		}
	}

	getFeedbackTypeFromUrl() {
		const url = window.location.search;
		let urlParams = new URLSearchParams(url);
		const feedbackParam = urlParams.get('fb');

		switch (feedbackParam) {
			case codes.THANK_YOU_URL_PARAM: {
				return codes.THANK_YOU;
			}
			case codes.DATA_QUALITY_URL_PARAM: {
				return codes.DATA_QUALITY;
			}
			case codes.OVER_QUOTA_URL_PARAM: {
				return codes.OVER_QUOTA;
			}
			case codes.TERMINATE_URL_PARAM: {
				return codes.TERMINATE;
			}
			case codes.ERROR_URL_PARAM: {
				return codes.ERROR;
			}
		}

		return "unknown";
	}

	handleViewerMetaDataOnClose(callback, preResultFunction) {
		this.handleViewerMetaData(this.props.currentUser, { reelchat: { optOut: true } }, callback, preResultFunction);
	}

	handleIntialRedirect(feedbackType, targetParams) {
		if (feedbackType === 'waitingroom') return;

		if (targetParams
			&& targetParams.partner
			&& !helpers.isQualtricsPartner(targetParams.partner)
		) {
			this.redirectUser(feedbackType, targetParams);
		} else if (targetParams
			&& targetParams.partner
			&& helpers.isQualtricsPartner(targetParams.partner)
		) {
			// save viewer metadata for qualtrics partner
			const redirectUrl = helpers.returnRedirectUrl(feedbackType, targetParams);
			const metadata = helpers.getViewerMetadata(this.props.currentUser, this.props.fetchedGroup);
			if (redirectUrl && metadata && !metadata.redirect) metadata.redirect = redirectUrl;
			if (metadata && !metadata.feedback_type) metadata.feedback_type = feedbackType;

			this.handleViewerMetaData(this.props.currentUser, metadata, () => {
				auth.logout();
				const feedback = { ...metadata, externalUrl: !!metadata.redirect };
				this.gotoRedirect(feedback);
			});
		}
	}

	async gotoRedirect(feedback) {
		if (feedback.externalUrl) {
			//Chromium browsers automatically redirect to Google Play if the app isn't installed.
			//So for this browser there is no need for user interaction to redirect the user to Google Play.

			const displayGooglePlayModal = (
				feedback.isAndroidChromeLink &&
				!this.installedChrome && (
					DeviceHelper.isFacebook ||
					DeviceHelper.isInstagram ||
					!DeviceHelper.isChromiumBrowser
				)
			);

			if (displayGooglePlayModal) {
				const intl = this.props.intl;

				document.removeEventListener('visibilitychange', this.onChangeVisibility);
				document.addEventListener('visibilitychange', this.onChangeVisibility);

				this.redirectTimeout = setTimeout(() => {
					window.swal.fire({
						title: intl.formatMessage(localeMessages.redirect_google_play_dialog_title),
						text: intl.formatMessage(localeMessages.redirect_google_play_dialog_message),
						icon: 'info',
						confirmButtonColor: '#00baa1'
					}).then((result) => {
						this.redirectToGooglePlay();
					});
				}, 2000);
			}
			window.location.href = feedback.redirect;
		}
		else if(feedback.reload) {
			window.location.reload();
		}
		else if(feedback.reelchatOptOut) {
			this.handleViewerMetaDataOnClose(() => { this.props.history.push(feedback.redirect); });
		} 
		else {
			this.props.history.push(feedback.redirect);
		}
	}

	storeStudyStatus(groupId, feedbackType) {
		Logger.log(this.constructor.name, "storeStudyStatus()", groupId, feedbackType);

		switch (feedbackType) {
			case codes.THANK_YOU:
				studyFns.addRecentStudyResults(groupId, "completed");
				break;

			case codes.DATA_QUALITY:
			case codes.TERMINATE:
			case codes.OVER_QUOTA:
			case codes.INELIGIBLE:
				studyFns.addRecentStudyResults(groupId, "screened");
				break;
		}
	}

	redirectUser(feedbackType, targetParams) {
		Logger.log(this.constructor.name, "redirectUser()", feedbackType, targetParams);

		const tracker = this.props.tracker ? this.props.tracker : ReactGA;
		let sessionStatus = new SessionStatus(SessionStatusEvent.FEEDBACK_REDIRECT, '', '', false, false, true);
		sessionStatus.gaTracker = tracker;
		sessionStatus.type = feedbackType ? feedbackType : this.getFeedbackTypeFromUrl();
		sessionStatus.state = targetParams && targetParams.partner ? targetParams.partner : 'Pilotly User';
		this.props.globalAnalyticsAdapter.reportAnalyticsEvent(sessionStatus);
		
		switch (feedbackType) {
			case codes.THANK_YOU:
				// redirect using redir url
				Logger.log(this.constructor.name, "redirectUser() thankyou", targetParams.redir);
				auth.logout();
				if (targetParams.redir) window.location.href = targetParams.redir;
				break;
			
			case codes.DATA_QUALITY:
				// redirect using dq url
				Logger.log(this.constructor.name, "redirectUser() dataquality", targetParams.dq);
				auth.logout();
				if (targetParams.dq) window.location.href = targetParams.dq;
				break;

			case codes.TERMINATE:
				// redirect using err url
				Logger.log(this.constructor.name, "redirectUser() terminate");
				auth.logout();
				if(targetParams.err) window.location.href = targetParams.err;
				break;

			case codes.OVER_QUOTA:
				// redirect user oq url
				Logger.log(this.constructor.name, "redirectUser() overquota", targetParams.oq);
				auth.logout();
				if(targetParams.oq) window.location.href = targetParams.oq;
				break;

			case codes.INVALID_URL:
				// redirect using err url
				Logger.log(this.constructor.name, "redirectUser() invalidUrl", targetParams.err);
				auth.logout();
				if(targetParams.err) window.location.href = targetParams.err;
				break;

			default: {
				Logger.log(this.constructor.name, "redirectUser() Missing type");
				auth.logout();
			}
		}
	}

	getFeedbackDisplay(feedbackType, targetParams, study) {
		Logger.log(this.constructor.name, "getFeedbackDisplay()", feedbackType, targetParams, study);

		const { intl } = this.props;

		let feedback = { title: "", message: "", redirect: null, redirectText: "Return", externalUrl: false, isButtonGrey: false }; // INTL
		let groupPoints = calculatePoints(study);
		let startDate = study && study.start_date ? moment(study.start_date).format('LL') : "another day"; // INTL
		const isQualtricsPartner = targetParams && targetParams.partner ? helpers.isQualtricsPartner(targetParams.partner) : false;

		const tracker = this.props.tracker ? this.props.tracker : ReactGA;
		let sessionStatus = new SessionStatus(SessionStatusEvent.RENDER_FEEDBACK_DISPLAY, '', '', false, false, true);
		sessionStatus.gaTracker = tracker;
		sessionStatus.type = feedbackType ? feedbackType : this.getFeedbackTypeFromUrl();
		this.props.globalAnalyticsAdapter.reportAnalyticsEvent(sessionStatus);
		
		const url = window.location.search;
		let urlParams = new URLSearchParams(url);
		const source = urlParams.get('source');

		if (feedbackType) auth.removeThirdPartyUrl();
		switch(feedbackType) {
			case "alreadycompleted": {
				if ((targetParams && targetParams.partner) || (targetParams && targetParams.dl)) {
					// let feedbackAuthor = targetParams.partner ? "" : targetParams.publisher;

					feedback.message = intl.formatMessage(localeMessages.already_completed_message);
					feedback.redirect = "/";
					feedback.redirectText = intl.formatMessage(localeMessages.already_completed_button);
					if (!isQualtricsPartner) auth.logout();

				} else {
					feedback.message = intl.formatMessage(localeMessages.already_completed_points_message, { groupPoints });
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.already_completed_points_button);
				}
				feedback.title = intl.formatMessage(localeMessages.already_completed_title);
				break;
			}
			case "thankyou": {
				if (source === REELCHAT_SOURCE_ID) {
					const partnerNameCookie = getCookieValue(PARTNER_NAME);
					let partnerName = partnerNameCookie ? partnerNameCookie : "Our partner";
					feedback.message = intl.formatMessage(localeMessages.thank_you_from_reelchat_message, {partnerName});
					feedback.redirect = "/logout";
					feedback.redirectText = intl.formatMessage(localeMessages.thank_you_button_redirect);
					if (!isQualtricsPartner) auth.logout();
				} else if (targetParams && targetParams.partner && targetParams.redir) {
					// redirect using redirectUser

				} else if (targetParams && targetParams.dl) {
					feedback.message = intl.formatMessage(localeMessages.thank_you_message, { publisher: targetParams.publisher });
					feedback.redirect = targetParams.redir || "/logout";
					feedback.externalUrl = targetParams.redir ? true : false;
					feedback.redirectText = targetParams.redir ? intl.formatMessage(localeMessages.thank_you_button_redirect) : intl.formatMessage(localeMessages.thank_you_button_home);
					if (!isQualtricsPartner) auth.logout();

				} else {
					feedback.message = intl.formatMessage(localeMessages.thank_you_points_message, { groupPoints });
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.thank_you_points_button);
				}
				feedback.title = intl.formatMessage(localeMessages.thank_you_title);
				break;
			}
			case "waitingroom": {
				feedback.title = intl.formatMessage(localeMessages.thank_you_waiting_room_title, { publisher: targetParams.publisher });
				feedback.message = intl.formatMessage(localeMessages.thank_you_waiting_room_message);
				break;
			}
			case "dismissed": {
				feedback.title = intl.formatMessage(localeMessages.dismissed_waiting_room_title, { publisher: targetParams.publisher });
				feedback.message = intl.formatMessage(localeMessages.dismissed_waiting_room_message);
				feedback.redirect = "/logout";
				feedback.redirectText = intl.formatMessage(localeMessages.dismissed_waiting_room_button_redirect);
				break;
			}
			case "dataquality": {
				if (source === REELCHAT_SOURCE_ID) {
					feedback.message = intl.formatMessage(localeMessages.data_quality_from_reelchat_message);
					feedback.redirect = "/logout";
					feedback.title = intl.formatMessage(localeMessages.data_quality_from_reelchat_title);
					feedback.redirectText = intl.formatMessage(localeMessages.data_quality_button_redirect);
					if (!isQualtricsPartner) auth.logout();
				} else if (targetParams && targetParams.partner && targetParams.dq) {
					// redirect using redirectUser

				} else if (targetParams && targetParams.dl) {
					feedback.message = intl.formatMessage(localeMessages.data_quality_message);
					feedback.redirect = targetParams.redir || "/";
					feedback.externalUrl = targetParams.redir ? true : false;
					feedback.redirectText = targetParams.redir ? intl.formatMessage(localeMessages.data_quality_button_redirect) : intl.formatMessage(localeMessages.data_quality_button_home);
					if (!isQualtricsPartner) auth.logout();

				} else {
					feedback.message = intl.formatMessage(localeMessages.data_quality_points_message);
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.data_quality_points_button);
				}

				feedback.title = feedback.title ? feedback.title : intl.formatMessage(localeMessages.data_quality_title);

				break;
			}
			case "terminate": {
				if (source === REELCHAT_SOURCE_ID) {
					feedback.message = intl.formatMessage(localeMessages.terminate_from_reelchat_message);
					feedback.redirect = "/logout";
					feedback.title = intl.formatMessage(localeMessages.terminate_from_reelchat_title);
					feedback.redirectText = intl.formatMessage(localeMessages.terminate_button_redirect);
					if (!isQualtricsPartner) auth.logout();
				} else if (targetParams && targetParams.partner) {
					feedback.message = intl.formatMessage(localeMessages.terminate_partner_message);
					feedback.redirect = targetParams.err || "/logout";
					feedback.externalUrl = targetParams.err ? true : false;
					feedback.redirectText = intl.formatMessage(localeMessages.terminate_partner_button);
					if (!isQualtricsPartner) auth.logout();
					
				} else if (targetParams && targetParams.dl) {
					feedback.message = intl.formatMessage(localeMessages.terminate_distribution_message, { publisher: targetParams.publisher });
					feedback.redirect = targetParams.err || "/logout";
					feedback.externalUrl = targetParams.err ? true : false;
					feedback.redirectText = intl.formatMessage(localeMessages.terminate_distribution_button);
					if (!isQualtricsPartner) auth.logout();

				} else {
					feedback.message = intl.formatMessage(localeMessages.terminate_message);
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.terminate_button);
				}
				feedback.title = feedback.title ? feedback.title : intl.formatMessage(localeMessages.terminate_title);
				break;
			}
			case "ineligible": {
				if (targetParams && targetParams.partner) {
					feedback.redirect = targetParams.err || "/logout";
					feedback.externalUrl = targetParams.err ? true : false;
					feedback.redirectText = intl.formatMessage(localeMessages.ineligible_partner_button);
					if (!isQualtricsPartner) auth.logout();

				} else if (targetParams && targetParams.dl) {
					feedback.redirect = "/logout";
					feedback.redirectText = intl.formatMessage(localeMessages.ineligible_distribution_button);
					if (!isQualtricsPartner) auth.logout();

				} else {
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.ineligible_button);
				}
				feedback.title = intl.formatMessage(localeMessages.ineligible_title);
				feedback.message = intl.formatMessage(localeMessages.ineligible_message);
				break;
			}
			case "overquota": {
				if (targetParams && targetParams.partner && targetParams.oq) {
					// redirect user oq url
				
				} else if (targetParams && targetParams.dl) {
					feedback.title = intl.formatMessage(localeMessages.overquota_title);
					feedback.message = intl.formatMessage(localeMessages.overquota_partner_message);
					feedback.redirect = targetParams.redir || "/logout";
					feedback.externalUrl = targetParams.redir ? true : false;
					feedback.redirectText = intl.formatMessage(localeMessages.overquota_partner_button);
					if (!isQualtricsPartner) auth.logout();
				
				} else {
					feedback.title = intl.formatMessage(localeMessages.overquota_title);
					feedback.message = intl.formatMessage(localeMessages.overquota_message);
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.overquota_button);
				}
				break;
			}
			case "notfound":{
				feedback.title = intl.formatMessage(localeMessages.notfound_title);
				feedback.message = intl.formatMessage(localeMessages.notfound_message);
				feedback.redirect = "/";
				feedback.redirectText = intl.formatMessage(localeMessages.notfound_button);
				break;
			}
			case "invalidUrl": {
				if (targetParams && targetParams.partner) {
					// redirect using err url

				} else if (targetParams && targetParams.dl) {
					feedback.message = intl.formatMessage(localeMessages.invalid_url_distribution_message);

				} else {
					feedback.message = intl.formatMessage(localeMessages.invalid_url_message);
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.invalid_url_button);
				}
				feedback.title = intl.formatMessage(localeMessages.invalid_url_title);
				break;
			}
			case "invalidemail": {
				feedback.title = intl.formatMessage(localeMessages.invalid_email_title);
				feedback.message = intl.formatMessage(localeMessages.invalid_email_message);
				feedback.redirect = "/";
				feedback.redirectText = intl.formatMessage(localeMessages.invalid_email_button);
				break;
			}
			case "studyclosed": {
				if (targetParams && targetParams.partner) {
					feedback.message = intl.formatMessage(localeMessages.study_closed_partner_message);
					feedback.redirect = targetParams.err || "/";
					feedback.externalUrl = targetParams.err ? true : false;
					feedback.redirectText = intl.formatMessage(localeMessages.study_closed_partner_button);

				} else if (targetParams && targetParams.dl) {
					feedback.message = intl.formatMessage(localeMessages.study_closed_distribution_message);
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.study_closed_distribution_button);

				} else {
					feedback.message = intl.formatMessage(localeMessages.study_closed_message);
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.study_closed_button);
				}
				feedback.title = intl.formatMessage(localeMessages.study_closed_title);
				break;
			}
			case "studynotstarted": {
				if (targetParams && targetParams.partner) {
					feedback.redirect = "/";
					feedback.redirectText = intl.formatMessage(localeMessages.study_not_started_partner_button);

				} else if (targetParams && targetParams.dl) {
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.study_not_started_distribution_button);

				} else {
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.study_not_started_button);
				}
				// INTL:DATE
				feedback.message = intl.formatMessage(localeMessages.study_not_started_message, { startDate });
				feedback.title = intl.formatMessage(localeMessages.study_not_started_title);
				break;
			}
			case "studypaused": {
				if (targetParams && targetParams.partner) {
					feedback.message = intl.formatMessage(localeMessages.study_paused_partner_message);

				} else if (targetParams && targetParams.dl) {
					feedback.message = intl.formatMessage(localeMessages.study_paused_distribution_message);
				
				} else {
					feedback.message = intl.formatMessage(localeMessages.study_paused_message);
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.study_paused_button);
				}
				feedback.title = intl.formatMessage(localeMessages.study_paused_title);
				break;
			}
			case "error": {
				feedback.title = intl.formatMessage(localeMessages.error_title);
				feedback.message = intl.formatMessage(localeMessages.error_message);
				feedback.redirect = "reload";
				feedback.redirectText = intl.formatMessage(localeMessages.error_button);
				feedback.reload = true;
				break;
			}
			case "badurl": {
				feedback.title = intl.formatMessage(localeMessages.badurl_title);
				feedback.message = intl.formatMessage(localeMessages.badurl_message);
				feedback.redirect = "reload";
				feedback.redirectText = intl.formatMessage(localeMessages.badurl_button);
				feedback.reload = true;
				break;
			}
			case "studyreentry": {
				feedback.redirectText = "";

				if (targetParams && targetParams.partner) {
					feedback.redirect = targetParams.err || "/logout";
					feedback.externalUrl = targetParams.err ? true : false;
					feedback.redirectText = intl.formatMessage(localeMessages.studyreentry_partner_button);
					if (!isQualtricsPartner) auth.logout();

				}
				else if (targetParams && targetParams.dl) {
					feedback.redirect = "/logout";
					feedback.redirectText = intl.formatMessage(localeMessages.studyreentry_distribution_button);
					if (!isQualtricsPartner) auth.logout();
				}
				else {
					feedback.redirect = "/home";
					feedback.redirectText = intl.formatMessage(localeMessages.studyreentry_button);
				}
				feedback.title = intl.formatMessage(localeMessages.studyreentry_title);
				feedback.message = intl.formatMessage(localeMessages.studyreentry_message);
				break;
			}
			case 'invalid_environment': {
				feedback = this.getInvalidEnvironmentDisplay(intl, study, targetParams);
				break;
			}
			default: {
				Logger.log(this.constructor.name, "getFeedbackDisplay() Missing type");
			}
		}
		return feedback;
	}

	getInvalidEnvironmentDisplay(intl, study, targetParams) {
		let feedback = {
			title: "",
			message: "",
			redirect: null,
			redirectText: "Return",
			externalUrl: false,
			isButtonGrey: false
		}; // INTL

		const panelSegmentIndex = parseInt(targetParams.ps);
		const panelSegments = study.panel_segment;

		if (panelSegments && panelSegments.length > panelSegmentIndex) {

			const panelSegment = panelSegments[panelSegmentIndex];
			const isDesktopStudy = studyFns.isDesktopEnabledStudy(panelSegment);
			const isMobileWebStudy = studyFns.isMobileWebEnabledStudy(panelSegment);

			let deviceTypeMessage;
			let deviceTypeTitleKey = localeMessages.invalid_device_detected_desktop;

			if (DeviceHelper.isMobile) {
				let deviceTypeMessageKey;
				if (DeviceHelper.isTablet) {
					deviceTypeMessageKey = localeMessages.invalid_device_detected_tablet;
					deviceTypeTitleKey = localeMessages.invalid_device_detected_tablet;
				}
				else {
					deviceTypeMessageKey = localeMessages.invalid_device_detected_mobile_phone;
					deviceTypeTitleKey = localeMessages.invalid_device_detected_mobile_phone;
				}
				deviceTypeMessage = intl.formatMessage(deviceTypeMessageKey);
			}
			else {
				deviceTypeMessage = intl.formatMessage(localeMessages.invalid_device_detected_laptop) + ' / ' + intl.formatMessage(localeMessages.invalid_device_detected_desktop);
			}

			const deviceTypeTitle = this.capitalizeText(intl.formatMessage(deviceTypeTitleKey));

			if (DeviceHelper.isDesktop) {
				const isFirefoxBrowser = helpers.isFirefoxBrowser(DeviceHelper.browser);

				//TODO: Add translations of these texts
				if (isDesktopStudy && isFirefoxBrowser) {
					const browserName = helpers.returnSimpleBrowserText(DeviceHelper.browser);
					feedback.message = intl.formatMessage(localeMessages.invalid_desktop_browser_message);
					feedback.title = intl.formatMessage(localeMessages.invalid_desktop_browser_title, { browserName });
				}
				else {
					feedback.title = intl.formatMessage(localeMessages.invalid_device_detected_title, { deviceType: deviceTypeTitle });
					feedback.message = intl.formatMessage(localeMessages.invalid_device_detected_message, { deviceType: deviceTypeMessage });
					feedback.type = 'invalid_device';
				}
			}
			else if (DeviceHelper.isMobile) {
				if (isMobileWebStudy) {
					if (DeviceHelper.isTablet) {
						feedback.title = intl.formatMessage(localeMessages.invalid_device_detected_title, { deviceType: deviceTypeTitle });
						feedback.message = intl.formatMessage(localeMessages.invalid_device_detected_message, { deviceType: deviceTypeMessage });
						feedback.type = 'invalid_device';
					}
					else {
						const isValidBrowser = (DeviceHelper.isAndroid && DeviceHelper.isChrome) || (DeviceHelper.isIOS && DeviceHelper.isSafari && !DeviceHelper.isSafariViewController);

						if (!isValidBrowser) {
							feedback.externalUrl = true;
							feedback.type = 'invalid_mobile_browser';

							const validBrowser = (DeviceHelper.isAndroid ? 'Chrome' : (DeviceHelper.isIOS ? 'Safari' : ''));
							feedback.redirectText = intl.formatMessage(localeMessages.invalid_mobile_browser_button, { validBrowser });

							const browser = (DeviceHelper.isAndroid ? `Google ${validBrowser}` : (DeviceHelper.isIOS ? `Apple's ${validBrowser}` : ''));
							feedback.message = intl.formatMessage(localeMessages.invalid_mobile_browser_message, { browser, validBrowser });

							feedback.title = intl.formatMessage(localeMessages.invalid_mobile_browser_title, { validBrowser: (DeviceHelper.isAndroid ? browser : (DeviceHelper.isIOS ? validBrowser : '')) });

							let studyLink = auth.getSavedUrl();

							const fullStudyLink = `${window.location.origin}${studyLink}`;

							if (DeviceHelper.isAndroid) {
								feedback.isAndroidChromeLink = true;

								feedback.subtitle = intl.formatMessage(localeMessages.invalid_mobile_browser_android_subtitle1);
								feedback.androidSubtitle2 = intl.formatMessage(localeMessages.invalid_mobile_browser_android_subtitle2);

								//Android Intent URL to open the study link on Google Chrome when it is installed.
								//https://developer.chrome.com/docs/android/intents

								studyLink = `navigate?url=${fullStudyLink}`;

								const chromeLink = `intent://${studyLink}#Intent;action=android.intent.action.VIEW;package=com.android.chrome;scheme=googlechrome;end`;
								Logger.log(this.constructor.name, 'getInvalidEnvironmentDisplay()', 'chromeLink', chromeLink);
								feedback.redirect = chromeLink;
							}
							else {
								feedback.subtitle = intl.formatMessage(localeMessages.invalid_mobile_browser_ios_subtitle);
								
								//x-safari- is the custom scheme registered by Safari on iOS to open links from other apps.
								//https://christiantietze.de/posts/2023/05/safari-for-mac-url-scheme/
								const safariLink = `x-safari-${fullStudyLink}`;
								feedback.redirect = safariLink;
							}
						}
					}
				}
				else {
					feedback.title = intl.formatMessage(localeMessages.invalid_device_detected_title, { deviceType: deviceTypeTitle });
					feedback.message = intl.formatMessage(localeMessages.invalid_device_detected_message, { deviceType: deviceTypeMessage });
					feedback.type = 'invalid_device';
				}
			}
			//Corner case: The environment is not desktop nor mobile device (smartphone or tablet)
			else if (!DeviceHelper.isDesktop) {
				feedback.title = 'Invalid Environment';
				feedback.message = `browser = ${DeviceHelper.browser}, device = ${DeviceHelper.device}, deviceType = ${DeviceHelper.deviceType}`;
			}
		}

		return feedback;
	}

	capitalizeText(text) {
		return text.charAt(0).toUpperCase() + text.substring(1);
	}

	onChangeVisibility() {
		if (document.visibilityState === 'hidden') {

			//Let's assume that when the user is redirected to Google Play he always will install Google Chrome.
			//This way when the user comes back and clicks the button again,
			//the redirect modal won't display again
			if (this.didShowRedirectDialog) {
				this.didShowRedirectDialog = false;
				this.installedChrome = true;
			}

			//Close all SweetAlert modals
			window.swal.close();

			//Clear the redirect timeout to prevent the redirect modal from showing if the user was redirected to Google Chrome
			if (this.redirectTimeout) {
				clearTimeout(this.redirectTimeout);
				this.redirectTimeout = null;
			}

			document.removeEventListener('visibilitychange', this.onChangeVisibility);
		}
	}

	redirectToGooglePlay() {
		this.didShowRedirectDialog = true;
		const chromePlayStoreLink = 'market://details?id=com.android.chrome';
		window.location = chromePlayStoreLink;
	}

	renderValidDevices(group, targetParams) {

		const intl = this.props.intl;

		const panelSegmentIndex = parseInt(targetParams.ps);
		const panelSegments = group.panel_segment;

		if (panelSegments && panelSegments.length > panelSegmentIndex) {
	
			let validDevices = [];

			const panelSegment = panelSegments[panelSegmentIndex];
			const studyDevices = panelSegment.study_devices;

			const desktop = this.capitalizeText(intl.formatMessage(localeMessages.invalid_device_detected_desktop));
			const laptop = this.capitalizeText(intl.formatMessage(localeMessages.invalid_device_detected_laptop));

			const laptopOrDesktop = `${laptop} / ${desktop}`;
			
			//When there are no devices selected for a study,
			//enable it to desktop
			const isReelChatStudy = helpers.isReelChatStudy(targetParams);
			if (!studyDevices || studyDevices.length === 0 || isReelChatStudy) {
				validDevices.push(laptopOrDesktop);
			}
			else {
				validDevices = studyDevices.map((studyDevice) => {
					if (studyDevice === 'desktop_web') {
						return laptopOrDesktop;
					}
					if (studyDevice === 'mobile_web') {
						return this.capitalizeText(intl.formatMessage(localeMessages.invalid_device_detected_mobile_phone));
					}
				});
			}

			const validDevicesItems = validDevices.map((validDevice) => {
				return (
					<li key={validDevice}>
						{
							validDevice
						}
					</li>
				);
			});

			return (
				<ul className="browser-default feedback-message mb-20">
					{
						validDevicesItems
					}
				</ul>
			);
		}

		return null;
	}

	render() {
		Logger.log(this.constructor.name, "render()", this.props, this.state);

		const feedbackType = this.state.feedbackType ? this.state.feedbackType : this.props.feedbackType;
		const fetchedGroup = this.props.fetchedGroup ? this.props.fetchedGroup : this.state.fetchedGroup;
		const targetParams = this.props.targetParams ? this.props.targetParams : this.state.targetParams;

		let feedback = this.getFeedbackDisplay(feedbackType, targetParams, fetchedGroup);

		return (
			<FadeIn>
				<div className="pilotly-feedback">
					<div className="row feedback-container">
						<div className="feedback-wrapper">
							<div className="col s12">
								<h4 className={"feedback-message " + (DeviceHelper.isMobile ? 'mb-15' : 'mb-20')}>
									{ feedback.title }
								</h4>

								<p className={"feedback-message" + (!DeviceHelper.isMobile ? ' mb-20' : '')}>
									{ feedback.message }
								</p>

								{
									this.props.feedbackType === 'invalid_environment' && feedback.type === 'invalid_device' ?
										this.renderValidDevices(fetchedGroup, targetParams)
										: ''
								}

								{this.interval ?
									<p className='waiting-time-dislay text-center'>
										Waiting for: {this.state.waitingTime >= 60 ? formatDuration(this.state.waitingTime, ':') : this.state.waitingTime + 's'}
									</p>
									: ''
								}
							</div>

							<div className={"col s12 " + (DeviceHelper.isMobile ? 'mt-20': 'mt-30') + (feedback.redirect ? "" : " display-none")}>
								<button className={"btn waves-effect waves-light" + (feedback.isButtonGrey ? " grey-btn" : "")}
									onClick={() => this.gotoRedirect(feedback)}>
									{ feedback.redirectText }
								</button>
							</div>

							{ feedback.subtitle ?

								<div className="col s12">

									<p className="feedback-message mt-10 mb-15">
										{ feedback.subtitle }
									</p>

									{ feedback.androidSubtitle2 ? 
										<p className="feedback-message">
											{ feedback.androidSubtitle2 }
										</p>
										: ''
									}
								</div>
								: ''
							}
						</div>
					</div>
				</div>
			</FadeIn>
		);
	}
}

const mapStateToProps = state => ({
	panelPartner: state.targetProcess.setPanelPartner,
	tracker: state.GlobalAnalyticsAdapterReducer.gaTracker,
	globalAnalyticsAdapter: state.GlobalAnalyticsAdapterReducer.globalAnalyticsAdapter,
});

function mapDispatchToProps(dispatch) {
	return bindActionCreators({ setPanelPartner }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(Feedback));



// WEBPACK FOOTER //
// ./src/components/global/Feedback.js